import React, { useState } from 'react';
import i18n from '../../utilities/i18n';
import { Formik, Form, FormikProps, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import log from '../../utilities/log';
import TextInputWithError from '../ui/forms/TextInputWithError';
import {
  Button,
  Highlight,
  HStack,
  IconBannersWarning,
  VStack,
  Text,
} from '@adc/parallax-component-library';
import { Dialog } from 'tamagui';
import './Modal.scss';
import { UserNote, SubjectNoteAddRequest } from 'adc-sirius-module-common';

const MAX_NOTE_LENGTH = 500;

type CreateSubjectNoteModalProps = {
  trigger?: React.ReactNode;
  onSuccess: (note: SubjectNoteAddRequest) => Promise<void>;
};

/**
 * Modal for creating a new note.
 * @param {React.ReactNode} trigger - The element that triggers the modal.
 * @param {function} onSuccess - Callback function to be called when note creation is successful
 * @returns {React.JSX.Element}
 */
export default function CreateSubjectNoteModal({
  trigger,
  onSuccess,
}: CreateSubjectNoteModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const initialValues: Partial<UserNote> = {
    note: '',
  };

  const formSchema = Yup.object().shape({
    note: Yup.string()
      .required(i18n.t('Form.genericRequired'))
      .trim()
      .max(
        MAX_NOTE_LENGTH,
        i18n.t('Form.genericMaxLength', { count: MAX_NOTE_LENGTH }),
      ),
  });

  const onSubmit = async (values: any, helpers: FormikHelpers<any>) => {
    const requestData: SubjectNoteAddRequest = {
      note: values.note,
    };

    return onSuccess(requestData)
      .then(() => {
        helpers.resetForm();
        setIsOpen(false);
        setErrorMessage('');
      })
      .catch((err) => {
        log.error(err);

        setErrorMessage(i18n.t('Modal_CreateNote.error_generic'));
      });
  };

  return (
    <Dialog open={isOpen}>
      <Dialog.Trigger asChild onPress={() => setIsOpen(true)}>
        {trigger}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay key="overlay" />
        <Dialog.Content key="content" width="75%">
          <VStack gap="$2">
            <Dialog.Title>
              <Text type="$h3.strong">{i18n.t('Modal_CreateNote.title')}</Text>
            </Dialog.Title>
            {errorMessage ? (
              <Highlight
                variant="danger"
                iconStart={
                  <IconBannersWarning
                    color="$neutral.0"
                    accentColor="$support.danger.default"
                  />
                }
                title={i18n.t('Modal_CreateNote.error_header')}
                body={errorMessage}
                testID="error-banner"
              />
            ) : null}
          </VStack>
          <Formik
            initialValues={initialValues}
            validationSchema={formSchema}
            onSubmit={onSubmit}
          >
            {(formikProps: FormikProps<any>) => (
              <Form noValidate className="modal-form-layout">
                <VStack
                  paddingVertical="$4"
                  gap="$2"
                  flex={1}
                  minHeight={0}
                  overflow="scroll"
                >
                  <TextInputWithError
                    label={i18n.t('Entity.note')}
                    name="note"
                    multiline
                    minLines={4}
                    maxLines={4}
                    maxLength={MAX_NOTE_LENGTH}
                    supportingText={i18n.t('Form.characterCount', {
                      count: (formikProps.values.note || '').length,
                      maxCount: MAX_NOTE_LENGTH,
                    })}
                    testID="note"
                  />
                </VStack>
                <HStack justifyContent="flex-end" gap="$2" flexShrink={0}>
                  <Button
                    label={i18n.t('Common.cancel')}
                    variant="secondary"
                    disabled={formikProps.isSubmitting}
                    onPress={() => {
                      setIsOpen(false);
                      setErrorMessage('');
                      formikProps.resetForm();
                    }}
                    testID="cancel-note-creation"
                  />
                  <Button
                    label={i18n.t('Common.save')}
                    disabled={formikProps.isSubmitting}
                    onPress={formikProps.submitForm}
                    variant="primary"
                    testID="save-note"
                    type="submit"
                  />
                </HStack>
              </Form>
            )}
          </Formik>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
